import React, { Component, ReactNode } from 'react'
import { computed, makeObservable, observable } from 'mobx'
import { observer } from 'mobx-react'
import { Moment } from 'moment'
import moment from 'moment'
import { SharedContent } from 'src/models/SharedContent'
import { sharedAppStateStore } from 'src/store/AppStateStore'
import {
  ContainerDiv,
  DefaultButton,
  DefaultLink,
  FjAvatar,
  FjCard,
  FjTable,
  FjText,
  FormHeaderText,
} from 'src/components/Common'
import { Check, Copy, Edit3, ExternalLink } from 'react-feather'
import { Asset } from 'src/models/Asset'
import { copyToClipboard, formatDuration } from 'src/utils/format'
import { Col, Popover, Row, Tooltip } from 'antd'
import { ViewWrapper } from 'src/components/Common/ViewWrapper'
import { AnalyticsSummaryCard } from 'src/components/Feed/AnalyticsSummaryCard'
import { Colors } from 'src/constants/colors'
import { SfMetadataDisplay } from 'src/components/Feed/SfMetadataFormFields'
import { Paths } from 'src/constants/navigation'
import { ContentTypeIcon } from 'src/utils/content'

type SharedContentFieldProps = {
  title: string
  value: ReactNode
  actionButton?: ReactNode
}

const SharedContentField = ({ title, value, actionButton }: SharedContentFieldProps) => {
  return (
    <ContainerDiv display="flex" flexDirection="column">
      <FjText textAlign="left" fontSize="small" color={Colors.tapa}>
        {title}
      </FjText>
      <ContainerDiv display="flex" justifyContent="space-between">
        <FjText textAlign="left">{value}</FjText>
        {actionButton}
      </ContainerDiv>
    </ContainerDiv>
  )
}

type SharedContentSessionViewer = { name?: string; email?: string }

type SharedContentSessionType = {
  downloadCount?: number
  name: string
  sessionDuration: string
  sessionTime: Moment
  contentType: 'asset' | 'deal_room'
  contentId: string
  contentTitle: string
  viewer: SharedContentSessionViewer
}

interface ISharedContentSessionsTableProps {
  sharedContent: SharedContent
  sharedContentSaved?: (sharedContent: SharedContent) => void
}

@observer
export class SharedContentSessionsTable extends React.Component<ISharedContentSessionsTableProps> {
  @observable viewState: 'initialLoad' | 'loading' | 'error' | 'idle' = 'initialLoad'
  @observable data: { sessions: SharedContentSessionType[]; totalDurationViewed?: number; totalViews?: string } = {
    sessions: [],
  }

  @computed get summaryCardData() {
    return [
      { title: 'Total Views', body: this.data.totalViews },
      { title: 'Total View Time', body: formatDuration(this.data.totalDurationViewed) },
    ]
  }

  constructor(props: ISharedContentSessionsTableProps) {
    super(props)
    makeObservable(this)
  }

  async componentDidMount() {
    try {
      const { sharedContent } = this.props
      this.viewState = 'loading'
      this.data = sharedContent.id ? await sharedContent.getSessions() : { sessions: [] }
      this.viewState = 'idle'
    } catch (err) {
      this.viewState = 'error'
      sharedAppStateStore.handleError(err, { alert: false })
    }
  }

  getColumns = () => {
    return [
      {
        title: 'Date/Time',
        dataIndex: 'sessionTime',
        key: 'sessionTime',
        width: 140,
        render: (sessionTime: string) => `${moment(sessionTime).format('MM-DD-YYYY  hh:mm A')}`,
        sorter: (a, b) => {
          const dateA = new Date(a.sessionTime)
          const dateB = new Date(b.sessionTime)
          return dateB.getTime() - dateA.getTime()
        },
        onCell: () => ({ style: { whiteSpace: 'nowrap' as const } }),
      },
      {
        title: 'Viewer',
        key: 'viewer',
        dataIndex: 'viewer',
        width: 140,
        render: (viewer: SharedContentSessionViewer) => {
          if (viewer.name && viewer.email)
            return (
              <Popover placement="top" showArrow content={viewer.email}>
                <FjText cursor="pointer">{viewer.name}</FjText>
              </Popover>
            )
          if (viewer.name || viewer.email) return viewer.name || viewer.email
          return 'N/A'
        },
      },
      {
        title: 'Title',
        key: 'contentTitle',
        dataIndex: 'contentTitle',
        render: (contentTitle: string, record: SharedContentSessionType) => (
          <ContainerDiv display="flex" alignItems="center" gap={8}>
            <Tooltip title={record.contentType === 'asset' ? 'Asset' : 'Deal Room'}>
              <ContentTypeIcon
                contentType={record.contentType === 'deal_room' ? 'dealroom' : record.contentType}
                size={18}
              />
            </Tooltip>
            <FjText textAlign="left">{contentTitle}</FjText>
          </ContainerDiv>
        ),
        sorter: (a, b) => a.contentTitle.localeCompare(b.contentTitle),
      },
      {
        title: 'Duration',
        key: 'sessionDuration',
        render: ({ downloadCount, sessionDuration }) => {
          if (downloadCount === 1) return '--:--:--'
          return parseFloat(sessionDuration) ? moment.utc(sessionDuration * 1000).format('HH:mm:ss') : '~5s'
        },
        sorter: (a, b) => a.sessionDuration - b.sessionDuration,
      },
      {
        title: 'Is Download',
        dataIndex: 'downloadCount',
        key: 'downloadCount',
        render: (count) => (count === 1 ? <Check size={16} /> : ''),
        sorter: (a, b) => a.downloadCount - b.downloadCount,
      },
    ]
  }

  handleSharedLinkEdit = () => {
    const closeSharedContentModal = () => (sharedAppStateStore.editSharedContentModalProps = undefined)
    sharedAppStateStore.editSharedContentModalProps = {
      obj: this.props.sharedContent,
      onSuccess: (sharedContent: SharedContent) => {
        closeSharedContentModal()
        const closeSharedContentSessionModal = () => (sharedAppStateStore.sharedContentSessionModalProps = undefined)
        sharedAppStateStore.sharedContentSessionModalProps = {
          onCancel: closeSharedContentSessionModal,
          onSuccess: closeSharedContentSessionModal,
          sharedContent,
          sharedContentSaved: this.props.sharedContentSaved,
        }
        this.props.sharedContentSaved?.(sharedContent)
      },
      onCancel: closeSharedContentModal,
    }
  }

  render() {
    const { sharedContent } = this.props
    return (
      <ViewWrapper viewState={this.viewState} customErrorUI="Failed to load sessions">
        <FjCard backgroundColor={Colors.athensGray} marginBottom="17px" textAlign="left" position="relative">
          <DefaultButton
            style={{ position: 'absolute', right: 16, top: 16 }}
            clicked={this.handleSharedLinkEdit}
            image={<Edit3 color={Colors.cottonSeed} size={20} />}
          />
          <ContainerDiv marginBottom="16px" textAlign="left">
            <FjText textAlign="left" fontSize="medium" fontWeight="bold">
              {sharedContent.title}
            </FjText>
          </ContainerDiv>
          <SfMetadataDisplay obj={sharedContent} />
          <Row gutter={[32, 12]}>
            <Col xs={24} md={12}>
              <SharedContentField
                title="Content"
                value={sharedContent.learningContent.title}
                actionButton={
                  <ExternalLink
                    size={16}
                    color={Colors.cottonSeed}
                    cursor="pointer"
                    onClick={() => {
                      window.open(
                        sharedContent.learningContent instanceof Asset
                          ? Paths.getClassroomPath({ assetId: sharedContent.learningContent.id })
                          : Paths.getDealRoomPath({ id: sharedContent.learningContent.id }),
                        '_blank'
                      )
                    }}
                  />
                }
              />
            </Col>
            <Col xs={24} md={12}>
              <SharedContentField
                title="Expiration Date"
                value={sharedContent.expiryDate ? sharedContent.expiryDate.format('MM-DD-YYYY') : 'N/A'}
              />
            </Col>
            <Col xs={24} md={12}>
              <SharedContentField
                title="URL"
                value={sharedContent.getShareLink()}
                actionButton={
                  <Copy
                    size={16}
                    color={Colors.cottonSeed}
                    cursor="pointer"
                    onClick={() =>
                      copyToClipboard(sharedContent.getShareLink(), undefined, `Link to ${sharedContent.title}`)
                    }
                  />
                }
              />
            </Col>
            <Col xs={24} md={12}>
              <SharedContentField
                title="Author"
                value={
                  <DefaultLink to={Paths.getProfilePath(sharedContent.author.id)}>
                    <ContainerDiv display="flex" alignItems="center">
                      <FjAvatar size={20} author={sharedContent.author} style={{ marginRight: '10px' }} />
                      <FjText>{sharedContent.author.fullName}</FjText>
                    </ContainerDiv>
                  </DefaultLink>
                }
              />
            </Col>
            <Col xs={24} md={12}>
              <SharedContentField
                title="Password"
                value={sharedContent.password ? '••••••' : '-'}
                actionButton={
                  sharedContent.password && (
                    <Copy
                      size={16}
                      color={Colors.cottonSeed}
                      cursor="pointer"
                      onClick={() =>
                        copyToClipboard(
                          sharedContent.password,
                          'Password copied to clipboard!',
                          `${sharedContent.title} Password`
                        )
                      }
                    />
                  )
                }
              />
            </Col>
            <Col xs={24} md={12}>
              <SharedContentField title="Require Name & Email" value={sharedContent.requireViewerInfo ? 'Yes' : 'No'} />
            </Col>
          </Row>
        </FjCard>
        <Row gutter={[20, 10]} justify="center" style={{ marginBottom: '32px' }}>
          {this.summaryCardData.map(({ title, body }) => (
            <Col xs={24} md={12} key={`${title}${body}`}>
              <AnalyticsSummaryCard title={title} body={body} />
            </Col>
          ))}
        </Row>
        <ContainerDiv marginBottom="16px" textAlign="left">
          <FjText textAlign="left" fontSize="medium" fontWeight="bold">
            Sessions
          </FjText>
        </ContainerDiv>
        <FjTable columns={this.getColumns()} dataSource={this.data.sessions} pagination={false} />
      </ViewWrapper>
    )
  }
}

export interface ISharedContentSessionsModalProps {
  onSuccess: () => void
  onCancel: () => void
  sharedContent: SharedContent
  sharedContentSaved?: (sharedContent: SharedContent) => void
}

export class SharedContentSessionsModal extends Component<ISharedContentSessionsModalProps> {
  render() {
    return (
      <ContainerDiv>
        <FormHeaderText heading={`Shared Link: "${this.props.sharedContent.title}"`} />
        <SharedContentSessionsTable
          sharedContent={this.props.sharedContent}
          sharedContentSaved={this.props.sharedContentSaved}
        />
      </ContainerDiv>
    )
  }
}
